/**
 * @voltxampere/presets — RC bandpass filter (Sprint 50-4)
 *
 * Series RC high-pass cascaded with shunt RC low-pass produces a
 * bandpass response. With R1=10kΩ + C1=10nF (high-pass corner ~1.6kHz)
 * and R2=1.6kΩ + C2=10nF (low-pass corner ~10kHz), the passband sits
 * between ~1.6kHz and ~10kHz.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { makeCircuit, makeNet, makeComponent } from "../_builders.js";
export const rcBandpass = {
    id: "rc-bandpass",
    name: { tr: "RC Bant Geçiren Filtre", en: "RC Bandpass Filter" },
    category: "filter",
    difficulty: 2,
    description: {
        tr: "Yüksek geçiren + alçak geçiren RC ile bant geçiren. Geçiş bandı ~1.6kHz-10kHz.",
        en: "High-pass + low-pass RC cascade. Passband ~1.6kHz-10kHz.",
    },
    circuit: makeCircuit({
        slot: 1,
        name: "RC Bandpass",
        description: "Cascaded RC bandpass filter",
        nets: [
            makeNet({ name: "gnd", isGround: true }),
            makeNet({ name: "v_in" }),
            makeNet({ name: "mid" }),
            makeNet({ name: "v_out" }),
        ],
        components: [
            makeComponent({
                id: "V1",
                type: "voltage-source",
                params: { voltage: 1 },
                terminals: { pos: "net_v_in", neg: "net_gnd" },
                label: "V1",
                position: { x: -200, y: 0 },
                rotation: 0,
            }),
            makeComponent({
                id: "C1",
                type: "capacitor",
                params: { capacitance: 10e-9 },
                terminals: { p1: "net_v_in", p2: "net_mid" },
                label: "C1",
                position: { x: -100, y: 0 },
                rotation: 0,
            }),
            makeComponent({
                id: "R1",
                type: "resistor",
                params: { resistance: 10000 },
                terminals: { p1: "net_mid", p2: "net_gnd" },
                label: "R1",
                position: { x: -40, y: 60 },
                rotation: 90,
            }),
            makeComponent({
                id: "R2",
                type: "resistor",
                params: { resistance: 1600 },
                terminals: { p1: "net_mid", p2: "net_v_out" },
                label: "R2",
                position: { x: 40, y: 0 },
                rotation: 0,
            }),
            makeComponent({
                id: "C2",
                type: "capacitor",
                params: { capacitance: 10e-9 },
                terminals: { p1: "net_v_out", p2: "net_gnd" },
                label: "C2",
                position: { x: 120, y: 60 },
                rotation: 90,
            }),
            makeComponent({
                id: "GND1",
                type: "ground",
                params: {},
                terminals: { p: "net_gnd" },
                position: { x: -200, y: 80 },
                rotation: 0,
            }),
        ],
        wires: [
            {
                id: "w_v_in",
                netId: "net_v_in",
                path: [
                    { x: -200, y: -10 },
                    { x: -200, y: -40 },
                    { x: -120, y: -40 },
                    { x: -120, y: 0 },
                ],
                style: "orthogonal",
            },
            {
                id: "w_mid",
                netId: "net_mid",
                path: [
                    { x: -80, y: 0 },
                    { x: 20, y: 0 },
                    { x: -40, y: 0 },
                    { x: -40, y: 40 },
                ],
                style: "orthogonal",
            },
            {
                id: "w_v_out",
                netId: "net_v_out",
                path: [
                    { x: 60, y: 0 },
                    { x: 120, y: 0 },
                    { x: 120, y: 40 },
                ],
                style: "orthogonal",
            },
            {
                id: "w_gnd",
                netId: "net_gnd",
                path: [
                    { x: -200, y: 10 },
                    { x: -200, y: 71 },
                    { x: 120, y: 71 },
                    { x: 120, y: 80 },
                ],
                style: "orthogonal",
            },
        ],
    }),
    expectedValues: [
        {
            node: "v_out",
            analysis: "dc",
            value: 0,
            tolerance: 0.01,
            unit: "V",
        },
    ],
    learningGoals: [
        {
            tr: "İki adet RC kademesi seri bağlanarak bant geçiren karakteristik elde edilir.",
            en: "Two RC stages in series produce a bandpass response.",
        },
        {
            tr: "DC analizinde kapasitör açık devre — V_out = 0.",
            en: "In DC, the capacitor is open — V_out = 0.",
        },
    ],
    references: ["Sedra/Smith, Microelectronic Circuits, Ch. 16"],
};
//# sourceMappingURL=rc-bandpass.js.map